<script lang="ts">
	import snarkdown from 'snarkdown';
	import { get } from 'svelte/store';
	import { push } from 'svelte-spa-router';
	import DOMPurify from 'dompurify';
	import { type ChatMessage } from '../../services/ai-scene-chat-api';
	import { aiSceneChatStore } from 'src/stores/features/ai-scene-chat';
	import { devices } from 'src/stores/lists/devices';
	import { groups } from 'src/stores/lists/groups';
	import { rooms } from 'src/stores/lists/rooms';
	import { scenes } from 'src/stores/lists/scenes';
	import { renderTokens, TOKEN_HIGHLIGHT_ENABLED } from './helpers';
	import SceneCreationAlert from './SceneCreationAlert.svelte';
	import DevicesUsedList from './DevicesUsedList.svelte';
	import QuickActions from './QuickActions.svelte';
	import { drawerState, drawerRef } from 'src/stores/ui/sidepanel-public';
	import './styles/message-content.css';

	export let message: ChatMessage;
	export let onRetry: (() => void) | undefined;
	export let onQuickAction: ((message: string) => void) | undefined;
	export let onExplainMore: (() => void) | undefined;

	$: isUser = message.role === 'user';
	$: isErrorMessage = message.isError === true;
	$: hasQuickActions = (message.quickActions?.length ?? 0) > 0;
	$: showExplainMore = message.needsExplanation && !isErrorMessage && !hasQuickActions;
	$: timestamp = new Date(message.timestamp).toLocaleTimeString('en-US', {
		hour: '2-digit',
		minute: '2-digit',
	});

	// Get room and device names for token rendering
	$: roomNames = Object.values($rooms).map((r: any) => r.name as string);
	$: deviceNames = Object.values($devices).map((d: any) => d.name as string);
	$: groupNames = Object.values($groups).map((g: any) => g.name as string);
	$: sceneNames = Object.entries($scenes).map(([id, s]: [string, any]) => s?._meta?.name || id);

	// Convert markdown to HTML - for both user and AI messages
	// For user messages, also render styled tokens (controlled by TOKEN_HIGHLIGHT_ENABLED flag)
	$: htmlContent = (() => {
		if (isUser) {
			if (TOKEN_HIGHLIGHT_ENABLED) {
				const withTokens = renderTokens(message.content, roomNames, deviceNames, {
					hideGlyphs: true,
					showIcons: true,
					groupNames,
					sceneNames,
				});
				return DOMPurify.sanitize(snarkdown(withTokens));
			}
			// Strip token glyphs (@, #, $, %) when highlighting is disabled
			const plainText = message.content.replace(/[@#$%](?=\S)/g, '');
			return DOMPurify.sanitize(snarkdown(plainText));
		}
		return snarkdown(message.content);
	})();

	// Only show devices list for device_control or scene intents where devices were actually controlled
	$: hasDevicesControlled = (message.devicesUsed?.length ?? 0) > 0;
	$: isRelevantIntent =
		message.intent === 'device_control' || message.intent === 'scene' || !message.intent;
	$: showDevicesList = hasDevicesControlled && isRelevantIntent;
	$: isDeviceControl = hasDevicesControlled && !message.sceneCreated;

	// Track local feedback state (will be synced with message.feedback)
	let feedbackGiven: 'up' | 'down' | null = message.feedback || null;

	function handleOpenScene(sceneId: string | undefined) {
		if (!sceneId) return;

		aiSceneChatStore.closeModal();
		push('/home/scenes/-1');

		setTimeout(() => {
			drawerState.set({ type: 'scene', id: sceneId });
			get(drawerRef).open();

			const card = document.getElementById(`scene-card-${sceneId}`);
			if (card) {
				card.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		}, 400);
	}

	// async function handleFeedback(sceneId: string | undefined, feedback: 'up' | 'down') {
	// 	if (!sceneId || feedbackGiven) return;

	// 	feedbackGiven = feedback;

	// 	// Update the message in the store with the feedback
	// 	aiSceneChatStore.updateMessage(message.timestamp, { feedback });

	// 	// Send feedback to backend (dev only — V2 prod has no feedback endpoint)
	// 	if (import.meta.env.DEV) {
	// 		try {
	// 			await fetch(`${AI_BACKEND_URL}/api/ai-scene/feedback`, {
	// 				method: 'POST',
	// 				headers: { 'Content-Type': 'application/json' },
	// 				body: JSON.stringify({
	// 					sceneId,
	// 					feedback,
	// 					timestamp: Date.now(),
	// 				}),
	// 			});
	// 		} catch (err) {
	// 			console.error('Failed to send feedback:', err);
	// 		}
	// 	}
	// }
</script>

<div
	class="message {isUser ? 'user' : 'ai'}"
	role="article"
	aria-label={isUser ? 'Your message' : 'AI response'}
>
	<div class="message-bubble">
		<div class="message-content" class:user-content={isUser}>
			<!-- Render markdown for both user and AI messages -->
			{@html DOMPurify.sanitize(htmlContent)}
		</div>

		<SceneCreationAlert
			sceneCreated={message.sceneCreated ?? false}
			sceneId={message.sceneId}
			onOpenScene={handleOpenScene}
		/>

		<!-- {#if showDevicesList}
			<DevicesUsedList devicesUsed={message.devicesUsed ?? []} {isDeviceControl} {onQuickAction} />
		{/if} -->

		<QuickActions
			quickActions={message.quickActions}
			{isErrorMessage}
			{showExplainMore}
			{onQuickAction}
			{onRetry}
			{onExplainMore}
		/>

		<!-- <div class="message-meta">
			<span class="timestamp">{timestamp}</span>
		</div> -->

		<!-- Token usage display (dev mode only) -->
		{#if message.usage && import.meta.env.DEV}
			<div class="token-info mt-s">
				<span class="token-stat">
					<i class="fas fa-coins" aria-hidden="true"></i>
					{message.usage.total_tokens} tokens
				</span>
				<span class="token-cost">
					${message.usage.cost_usd.toFixed(4)}
				</span>
			</div>
		{:else if message.processingTime && import.meta.env.DEV}
			<div class="token-info mt-s">
				<span class="token-stat">
					<i class="fas fa-clock" aria-hidden="true"></i>
					{message.processingTime}ms
				</span>
			</div>
		{/if}
	</div>
</div>

<style>
	.message {
		display: flex;
		margin-bottom: 12px;
		padding: 0 8px;
	}

	.message.user {
		justify-content: flex-end;
	}

	.message.ai {
		justify-content: flex-start;
	}

	.message-bubble {
		max-width: 85%;
		padding: 12px 16px;
		border-radius: 16px;
		position: relative;
	}

	.message.user .message-bubble {
		background: var(--color-secondary);
		border-bottom-right-radius: 4px;
	}

	.message.user .message-bubble .message-content {
		color: var(--color-base);
	}

	.message.ai .message-bubble {
		background: var(--color-1, #242527);
		color: var(--color-white, #ffffff);
		border-bottom-left-radius: 4px;
	}

	.message-content {
		word-wrap: break-word;
		line-height: 1.6;
		user-select: text;
		-webkit-user-select: text;
	}

	/* User message content styles */
	.message-content.user-content {
		color: var(--color-base, #151617);
	}

	/*
	 * Markdown styles (p, strong, code, pre, blockquote, h1-h4, ul, ol, li, hr, a)
	 * and token styles (.suggestion-token) are in styles/message-content.css
	 * because they target dynamically injected HTML via {@html} and cannot be scoped.
	 */

	/* Token info (dev mode only) */
	.token-info {
		display: flex;
		gap: 12px;
		padding: 6px 10px;
		background: rgba(255, 193, 7, 0.1);
		border: 1px dashed rgba(255, 193, 7, 0.3);
		border-radius: 4px;
		font-size: 11px;
		color: var(--color-warning, #ffc107);
	}

	.token-stat {
		display: flex;
		align-items: center;
		gap: 4px;
	}

	.token-cost {
		font-weight: 500;
	}

	/* Scene feedback animation is in styles/message-content.css */

	/* Responsive adjustments */
	@media (max-width: 768px) {
		.message-bubble {
			max-width: 90%;
			padding: 10px 12px;
		}
	}
</style>
